import { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useAuthStore } from "./../../context/useAuthStore";
import { HiMiniShoppingCart } from "react-icons/hi2";
import { Link } from "react-router-dom";
import { ROUTES } from "../../constant/routes";
import secureLocalStorage from "react-secure-storage";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

type Subject = {
  subscriptionId?: string | null;
  subjectId?: string;
  subjectCode?: string;
  semesterYear: string;
  semesterNumber: string;
  status: string;
  endDate: string;
  subjectName: string;
  price?: number;
};

type SubjectDataItem = {
  subjectId: string;
  subjectCode?: string;
  subjectName?: string;
};

const tableHeaders = ["Subject Name", "Price", "Status", "Active Till"];

const normalizeStatus = (s?: string) =>
  (s || "")
    .trim()
    .toLowerCase()
    .replace(/[_\-\s]+/g, "");

type MySubjectsProps = {
  subjectData?: SubjectDataItem[];
};

const MySubjects = ({ subjectData = [] }: MySubjectsProps) => {
  const { user, token, clearSession } = useAuthStore();
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState<Subject[]>([]);
  const [postingMissing, setPostingMissing] = useState(false);

  useEffect(() => {
    const storedCart = secureLocalStorage.getItem("subjectCart");
    if (storedCart) {
      try {
        const parsed = JSON.parse(storedCart as string);
        setCart(Array.isArray(parsed) ? parsed : []);
      } catch {
        setCart([]);
      }
    }
  }, []);

  useEffect(() => {
    secureLocalStorage.setItem("subjectCart", JSON.stringify(cart));
  }, [cart]);

  const fetchSubjects = async () => {
    if (!user?.userId) {
      toast.error("User not found.");
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(
        `${API_BASE_URL}/subscription?userId=${encodeURIComponent(
          user.userId
        )}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );
      const subscribedSubject = res.data.success;
      if (subscribedSubject && Array.isArray(subscribedSubject)) {
        setSubjects(subscribedSubject);
        enrollMissingSubscriptions(subscribedSubject);
      } else {
        setSubjects([]);
        toast.error("No subjects found.");
        setTimeout(() => window.location.reload(), 2000);
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorMessage =
          error.response?.data?.message || "Failed to fetch subjects";
        if (
          (typeof errorMessage === "string" &&
            errorMessage.includes("Session expired")) ||
          error.response?.status === 401
        ) {
          clearSession("expired");
        } else {
          toast.error(errorMessage);
        }
      } else {
        toast.error("Failed to fetch subjects.");
      }
      setSubjects([]);
    } finally {
      setLoading(false);
    }
  };

  const enrollMissingSubscriptions = async (fetchedSubjects: Subject[]) => {
    if (!user?.userId) {
      toast.error("User not found — cannot enroll missing subscriptions.");
      return;
    }

    const missing = fetchedSubjects.filter(
      (s) =>
        !s.subscriptionId ||
        s.subscriptionId === "" ||
        s.subscriptionId === null
    );

    if (missing.length === 0) return;

    const payload = missing
      .map((s) => {
        const sid = s.subjectId;
        if (!sid) return null;
        const subjectCodeFromProp = subjectData.find(
          (sd) => sd.subjectId === sid
        )?.subjectCode;
        const subjectCode = s.subjectCode ?? subjectCodeFromProp ?? undefined;

        return {
          userId: user.userId,
          subjectId: sid,
          subjectCode,
        };
      })
      .filter(Boolean) as {
      userId: string;
      subjectId: string;
      subjectCode?: string;
    }[];

    if (payload.length === 0) {
      toast.error("Some subjects missing IDs — cannot auto-enroll.");
      return;
    }

    try {
      setPostingMissing(true);
      const res = await axios.post(`${API_BASE_URL}/subscription`, payload, {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const created = Array.isArray(res.data?.success)
        ? res.data.success
        : null;

      if (created && created.length > 0) {
        setTimeout(() => window.location.reload(), 2000);
      } else if (res.data?.success && typeof res.data.success === "string") {
        setTimeout(() => window.location.reload(), 2000);
      } else {
        setTimeout(() => window.location.reload(), 2000);
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorMessage =
          error.response?.data?.message ||
          "An error occurred while creating missing subscriptions";
        if (
          (typeof errorMessage === "string" &&
            errorMessage.includes("Session expired")) ||
          error.response?.status === 401
        ) {
          clearSession("expired");
        } else {
          toast.error(errorMessage);
        }
      } else {
        toast.error("An error occurred while creating missing subscriptions.");
      }
    } finally {
      setPostingMissing(false);
    }
  };

  useEffect(() => {
    fetchSubjects();
  }, [user?.userId, token, clearSession]);

  const toggleCart = (subject: Subject) => {
    const cartSubject: Subject = {
      subscriptionId:
        subject.subscriptionId ??
        `${subject.subjectName}_${subject.semesterYear}_${subject.semesterNumber}`,
      semesterYear: subject.semesterYear,
      semesterNumber: subject.semesterNumber,
      status: subject.status,
      endDate: subject.endDate,
      subjectName: subject.subjectName,
      price: subject.price ?? 500,
      subjectId: subject.subjectId,
      subjectCode: subject.subjectCode,
    };

    const exists = cart.some(
      (item) =>
        (item.subscriptionId &&
          cartSubject.subscriptionId &&
          item.subscriptionId === cartSubject.subscriptionId) ||
        item.subjectName === cartSubject.subjectName
    );

    if (exists) {
      setCart((prev) =>
        prev.filter(
          (item) =>
            !(
              (item.subscriptionId &&
                cartSubject.subscriptionId &&
                item.subscriptionId === cartSubject.subscriptionId) ||
              item.subjectName === cartSubject.subjectName
            )
        )
      );
      toast.success(`${cartSubject.subjectName} removed from cart`);
    } else {
      setCart((prev) => [...prev, cartSubject]);
      toast.success(`${cartSubject.subjectName} added to cart`);
    }
  };

  const isInCart = (subject: Subject) =>
    cart.some(
      (item) =>
        (item.subscriptionId &&
          subject.subscriptionId &&
          item.subscriptionId === subject.subscriptionId) ||
        item.subjectName === subject.subjectName
    );

  const formatPrice = (p?: number) =>
    typeof p === "number"
      ? p.toLocaleString("en-IN", { maximumFractionDigits: 2 })
      : "N/A";

  return (
    <div className="subjectSection">
      <div className="flex justify-between items-center gap-2 mb-2">
        <h2 className="subjectTitle">My Subjects</h2>
        <Link
          to={ROUTES.CHECKOUT}
          className="relative inline-flex items-center p-3 text-sm font-medium text-center text-white bg-myLabelText rounded-lg hover:bg-myButtonBg"
          aria-label="Go to checkout"
        >
          <HiMiniShoppingCart />
          <div className="absolute inline-flex items-center justify-center w-6 h-6 text-xs font-semibold text-white bg-red-500 border-2 border-white rounded-full -top-2 -end-2">
            {cart.length}
          </div>
        </Link>
      </div>
      <div className="borderLine"></div>
      {loading ? (
        <p className="subjectNotFound" role="status">
          Loading...
        </p>
      ) : subjects.length === 0 ? (
        <p className="subjectNotFound" role="alert">
          No Subjects found.
        </p>
      ) : (
        <section className="subjectTableSection">
          <div className="overflow-x-auto">
            <div className="max-h-[calc(100vh-17.75rem)] overflow-y-auto">
              <table
                className="min-w-full border-collapse"
                aria-label="My Subjects Table"
              >
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="tableHeadRow">
                    {tableHeaders.map((header, index) => (
                      <th key={index} className="tableHeadValue">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {subjects.map((sub, index) => {
                    const statusNorm = normalizeStatus(sub.status);
                    const isRegistered =
                      statusNorm === "registered" ||
                      statusNorm === "accessiblesub" ||
                      statusNorm === "accessible";
                    const isSubscribed = statusNorm === "subscribed";
                    const isExpired = statusNorm === "expired";
                    return (
                      <tr
                        key={
                          sub.subscriptionId ?? `${sub.subjectName}_${index}`
                        }
                        className="tableBodyRow"
                      >
                        <td className="tableBodyValue whitespace-break-spaces">
                          {sub.subjectName}
                        </td>
                        <td className="tableBodyValue whitespace-break-spaces">
                          {formatPrice(sub.price)}
                        </td>
                        <td className="tableBodyValue">
                          {isRegistered ? (
                            <div className="flex items-center gap-2">
                              <span className="inline-block px-4 py-1 rounded-full bg-gray-200 text-gray-700 font-semibold">
                                Registered
                              </span>
                              <button
                                type="button"
                                onClick={() => toggleCart(sub)}
                                className={`defaultButton w-auto font-medium text-sm px-4 py-1 ${
                                  isInCart(sub)
                                    ? "bg-red-500 hover:bg-red-600 text-white"
                                    : "bg-myLabelText hover:bg-myButtonBg text-white"
                                }`}
                                aria-label={
                                  isInCart(sub) ? "Remove" : "Add to Cart"
                                }
                              >
                                <HiMiniShoppingCart className="inline mr-2 mb-1" />
                                {isInCart(sub) ? "Remove" : "Add to Cart"}
                              </button>
                            </div>
                          ) : isSubscribed ? (
                            <div className="flex items-center gap-2">
                              <span className="inline-block px-4 py-1 rounded-full bg-green-200 text-green-700 font-semibold">
                                Subscribed
                              </span>
                            </div>
                          ) : isExpired ? (
                            <span className="inline-block px-4 py-1 rounded-full bg-red-200 text-red-700 font-semibold">
                              Expired
                            </span>
                          ) : (
                            sub.status
                          )}
                        </td>
                        <td className="tableBodyValue">
                          {sub.endDate
                            ? new Date(sub.endDate).toLocaleDateString("en-GB")
                            : "N/A"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {postingMissing && (
                <p className="mt-2 text-sm text-gray-600">
                  Creating missing subscriptions...
                </p>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default MySubjects;
